<template>
    <div class="ask-offer" data-testid="ask-box-offer">
        <Button
            class="ask-offer__button"
            data-testid="ask-box-offer-button"
            size="small"
            icon="pi pi-comments"
            :label="offer.title"
            :loading="isStarting"
            :disabled="isStarting"
            @click="start"
        />
    </div>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import { useToast } from "primevue/usetoast";
import { ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import type { ThreadOffer } from "@/lib/openapi/gen/types.gen";
import { ThreadContextKind } from "@/lib/openapi/gen/types.gen";
import { entityLocation } from "@/router/entity-location";
import { useThreadsStore } from "@/stores/threads-store";

/**
 * The one button the box offers when it has declined to change something.
 *
 * The box only reads; a change goes through a thread. The refusal already says
 * so in prose, and this is the same offer in a form that can be acted on —
 * which is the whole reason the answer carries a structured `offer_thread`
 * rather than leaving the UI to find the offer in English.
 *
 * **Consent is satisfied before the click, not after it.** The epic requires
 * that nothing from a private conversation reaches a thread without the exact
 * words being shown first. The first post is the user's own sentence, which the
 * box is already displaying above the answer, and the thread's title is the
 * label on this button. Both strings are on screen, so pressing it is the
 * consent — and the epic's "one button" survives intact.
 *
 * Its own component rather than markup in `AskBox.vue` for the same reason as
 * `AskReferences.vue`: it needs a router, and that file's spec mounts without
 * one.
 */
const props = defineProps<{
    offer: ThreadOffer;
    /** The user's own sentence. Becomes the thread's first post, verbatim. */
    question: string;
}>();

const emit = defineEmits<{ handedOff: [] }>();

const threads = useThreadsStore();
const router = useRouter();
const route = useRoute();
const toast = useToast();

const isStarting = ref(false);

/**
 * Create the thread, post the user's sentence into it, and go there.
 *
 * Modelled on `threads-store.startProcessBpmnThread`, which is the same shape:
 * the entity goes in twice — once as the thread's subject, once as an ENTITY
 * context item for the agent — and the opening sentence is posted with
 * `contribute` rather than passed to `createThread`. That is not a detail: a
 * kind-less thread does not dispatch its agent on creation, so without the
 * contribution the thread would sit there with nobody working on it.
 *
 * `kind` is left unset on purpose. The three onboarding/research kinds demand
 * stek-manager on the endpoint, while a plain thread needs only the `stek.user`
 * the ask box already required to answer at all.
 */
const start = async () => {
    if (isStarting.value) {
        return;
    }
    isStarting.value = true;

    const entityType = props.offer.entity_type ?? undefined;
    const entityId = props.offer.entity_id ?? undefined;
    const hasEntity = Boolean(entityType && entityId);

    /** Where the reader should end up, once the thread exists. */
    const land = (threadId: string) => {
        // Land in the draft with the thread beside it. Both halves of that come
        // from one push: the application layout watches `?thread=`, and on
        // seeing it switches reads to the thread's git branch *and* opens the
        // drawer. Navigating and then opening the drawer by hand would do the
        // same thing twice.
        const target = hasEntity
            ? entityLocation(
                  {
                      entity_type: entityType as string,
                      entity_id: entityId as string,
                  },
                  {
                      orgName: route.params.orgName,
                      stekName: route.params.stekName,
                  }
              )
            : undefined;

        if (target) {
            return router.push({
                ...(target as object),
                query: { thread: threadId },
            });
        }
        // A title-only offer, or an entity with no page of its own. There is
        // nowhere to stand, so open the thread where the reader already is
        // rather than sending them somewhere arbitrary.
        threads.openDrawer(threadId);
        return Promise.resolve();
    };

    let thread;
    try {
        thread = await threads.createThread(
            props.offer.title,
            undefined,
            undefined,
            entityType,
            entityId,
            hasEntity
                ? [
                      {
                          kind: ThreadContextKind.ENTITY,
                          data: {
                              entity_type: entityType as string,
                              entity_id: entityId as string,
                          },
                      },
                  ]
                : undefined
        );
    } catch (e) {
        // The shared `api` singleton is `throwOnError: true`, so a non-2xx
        // rejects here rather than coming back as a value — which means
        // `createThread`'s own error toast never runs. Without this the button
        // would spin, stop, and say nothing, and the reader's only available
        // reading is that it is broken.
        console.error("Could not start a thread from the ask box", e);
        thread = undefined;
    } finally {
        isStarting.value = false;
    }

    if (!thread?.identity) {
        toast.add({
            severity: "error",
            summary: "Could not start a thread",
            detail: "Nothing was created. Try again in a moment.",
            life: 5000,
        });
        return;
    }

    // The thread exists from here on, so every path below still lands the
    // reader on it. An invisible thread is worse than a flawed one.
    try {
        await threads.contribute(thread.identity, props.question);
    } catch (e) {
        // The thread was created but the opening sentence did not land. That
        // leaves a thread with no entries at all and so no agent working on it
        // — the exact state the contribution exists to prevent. Take the reader
        // there anyway and say what is missing: they can post it themselves,
        // which is what wakes the agent.
        console.error("Could not post the opening message", e);
        toast.add({
            severity: "warn",
            summary: "Thread created, but your message did not post",
            detail: "Send it again from the thread to get things moving.",
            life: 6000,
        });
    }

    await land(thread.identity);
    emit("handedOff");
};
</script>

<style scoped>
.ask-offer {
    display: flex;
}

/* Left-aligned and small: this is an offer, not a call to action. The answer
   above it is still the content, and the refusal has already been read by the
   time anyone looks down here. */
.ask-offer__button {
    font-size: 0.8rem;
}
</style>
